'use client';

import SvgChevronleft from '@common/__generated__/icon/SvgChevronleft';
import SvgChevronright from '@common/__generated__/icon/SvgChevronright';
import { Button } from '@common/components';
import { cn } from '@core/utils';
import type { GiftOrderProductReviewDto } from '@generated/auth';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';

import { ReviewListItem } from './ReviewListItem';

interface ReviewShortcutSectionProps {
  items: GiftOrderProductReviewDto[];
  reviewCount?: number;
  className?: string;
}

export const ReviewShortcutSection = ({
  items,
  reviewCount,
  className,
}: ReviewShortcutSectionProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
  });

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCurrentSlide(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect]);

  const CHUNK_SIZE = 6;

  //* 배열을 나누기
  const chunkArray = <T,>(array: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }

    return chunks;
  };

  const chunkedList = chunkArray(items, CHUNK_SIZE);
  const isNotRender = items.length < 1;

  if (isNotRender) return null;

  return (
    <section className={className}>
      <header className="mb-24 flex justify-between">
        <div className="basis-100" />
        <h2 className="text-center font-700 text-Gray-900 typo-h6">
          현재 {reviewCount}명의 기부자들이 후기를 남겼어요!
        </h2>
        <div className="flex basis-100 items-center justify-end gap-8">
          <p className="font-500 text-Gray-550 typo-body4">
            <span className="font-700 text-Gray-600">{currentSlide + 1}</span> /{' '}
            {chunkedList.length}
          </p>
          <div className="flex">
            <Button
              theme="sub-line"
              className="size-24 rounded-[2px] bg-Gray-white p-0"
              onClick={() => emblaApi?.scrollPrev()}
            >
              <SvgChevronleft className="size-18 text-Gray-500" />
            </Button>
            <Button
              theme="sub-line"
              className="size-24 rounded-[2px] bg-Gray-white p-0"
              onClick={() => emblaApi?.scrollNext()}
            >
              <SvgChevronright className="size-18 text-Gray-500" />
            </Button>
          </div>
        </div>
      </header>
      <div
        className={cn(
          'flex w-full justify-center rounded-large border-1 border-Gray-300 px-12 py-8',
        )}
      >
        <div
          className={cn('flex flex-col items-center gap-24 content-container')}
        >
          <div className={cn('w-full overflow-hidden')} ref={emblaRef}>
            <div className="flex">
              {chunkedList.map((chunk, chunkIndex) => (
                <div key={chunkIndex} className="min-w-0 flex-[0_0_100%]">
                  <div className={cn('grid grid-cols-3 gap-x-20 gap-y-16')}>
                    {chunk.map(
                      (
                        {
                          reviewId: review_id,
                          contents,
                          productName: product_name,
                          rating,
                          fileUrlList: file_url_list,
                        },
                        index,
                      ) => (
                        <ReviewListItem
                          key={`${chunkIndex}-${index}`}
                          reviewId={review_id}
                          productThumbnailUrl={file_url_list[0]}
                          productName={product_name}
                          rating={rating}
                          reviewContent={contents}
                        />
                      ),
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
